import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PenLine, Download } from 'lucide-react';
import { RaceFormat, RaceStatus } from '@/gql/graphql';
import type { SearchUtmbRacesQuery } from '@/gql/graphql';
import { Page } from '@/components/layout/Page';
import { RaceForm, type RaceFormData } from '@/components/races/RaceForm';
import { UtmbRaceSearch } from '@/components/races/UtmbRaceSearch';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageContent } from '@/components/layout/PageContent';
import { CREATE_RACE, CREATE_UTMB_RACE } from '@/graphql/mutations';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/utils';

type UtmbRace = NonNullable<SearchUtmbRacesQuery['searchUtmbRaces']>[number];

type Step = 'mode-select' | 'form';
type Mode = 'manual' | 'utmb';

const UTMB_CATEGORY_TO_FORMAT: Record<string, RaceFormat> = {
    '20k': RaceFormat['20K'],
    '50k': RaceFormat['50K'],
    '100k': RaceFormat['100K'],
    '100m': RaceFormat['100M'],
};

export function CreateRacePage() {
    const navigate = useNavigate();
    const { t } = useTranslation('races');
    const { toast } = useToast();

    const [step, setStep] = useState<Step>('mode-select');
    const [mode, setMode] = useState<Mode | null>(null);
    const [selectedUtmbRace, setSelectedUtmbRace] = useState<UtmbRace | null>(null);

    const [createRace, { loading: creatingManual }] = useMutation(CREATE_RACE);
    const [createUtmbRace, { loading: creatingUtmb }] = useMutation(CREATE_UTMB_RACE);

    const isLoading = creatingManual || creatingUtmb;

    const handleSelectManual = () => {
        setMode('manual');
        setStep('form');
    };

    const handleSelectUtmbRace = (race: UtmbRace) => {
        setSelectedUtmbRace(race);
        setMode('utmb');
        setStep('form');
    };

    const handleBack = () => {
        if (step === 'form') {
            setStep('mode-select');
            setMode(null);
            setSelectedUtmbRace(null);
        } else {
            navigate('/races');
        }
    };

    const handleManualCreate = async (formData: RaceFormData) => {
        try {
            const { data } = await createRace({
                variables: {
                    input: {
                        name: formData.name,
                        description: formData.description,
                        raceFormat: formData.raceFormat,
                        distance: formData.distance,
                        elevationGain: formData.elevationGain,
                        bettingDeadline: formData.bettingDeadline,
                        visibility: formData.visibility,
                        status: RaceStatus.Open,
                        totalRunners: formData.totalRunners,
                        tokenCount: formData.tokenCount,
                    },
                },
            });
            if (data?.createRace?.id) {
                navigate(`/races/${data.createRace.id}/add-runner`, {
                    replace: true,
                    state: { fromCreation: true },
                });
            }
        } catch (error) {
            console.error(error);
            toast(t('create.error'), 'error');
        }
    };

    const handleUtmbCreate = async (formData: RaceFormData) => {
        if (!selectedUtmbRace?.uri) return;
        try {
            const { data } = await createUtmbRace({
                variables: {
                    input: {
                        utmbUri: selectedUtmbRace.uri,
                        name: formData.name,
                        description: formData.description,
                        visibility: formData.visibility,
                        bettingDeadline: formData.bettingDeadline,
                        totalRunners: formData.totalRunners,
                    },
                },
            });
            if (data?.createUtmbRace?.id) {
                navigate(`/races/${data.createUtmbRace.id}/add-runner`, {
                    replace: true,
                    state: { fromCreation: true },
                });
            }
        } catch (error) {
            console.error(error);
            toast(t('create.error'), 'error');
        }
    };

    const utmbInitialValues: Partial<RaceFormData> | undefined = selectedUtmbRace
        ? {
              name: `${selectedUtmbRace.name ?? ''} ${selectedUtmbRace.startDate?.split('-')[0] ?? ''}`.trim(),
              raceFormat: UTMB_CATEGORY_TO_FORMAT[selectedUtmbRace.category?.toLowerCase() ?? ''] ?? RaceFormat['100K'],
              distance: selectedUtmbRace.distance,
              elevationGain: selectedUtmbRace.elevationGain,
              bettingDeadline: selectedUtmbRace.startDate ?? undefined,
          }
        : undefined;

    return (
        <Page>
            <PageHeader
                title={t('create.title')}
                onBack={handleBack}
                width="narrow"
            />

            <PageContent width="narrow">
                {step === 'mode-select' && (
                    <div className="space-y-6">
                        <h2 className="text-lg font-bold text-slate-900">{t('create.modeSelect.title')}</h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={handleSelectManual}
                                className="flex items-center gap-3 p-3 sm:flex-col sm:items-center sm:gap-3 sm:p-6 rounded-2xl border-2 border-slate-100 bg-white hover:border-emerald-300 hover:bg-emerald-50/30 transition-all"
                            >
                                <div className="p-2 sm:p-3 rounded-full bg-slate-100 text-slate-600 shrink-0">
                                    <PenLine size={20} className="sm:hidden" />
                                    <PenLine size={24} className="hidden sm:block" />
                                </div>
                                <div className="text-left sm:text-center">
                                    <span className="font-bold text-slate-900 text-sm sm:text-base">{t('create.modeSelect.manual')}</span>
                                    <span className="block text-xs text-slate-500">{t('create.modeSelect.manualDesc')}</span>
                                </div>
                            </button>

                            <button
                                type="button"
                                onClick={() => setMode('utmb')}
                                className={cn(
                                    "flex items-center gap-3 p-3 sm:flex-col sm:items-center sm:gap-3 sm:p-6 rounded-2xl border-2 transition-all",
                                    mode === 'utmb'
                                        ? "border-emerald-500 bg-emerald-50/50"
                                        : "border-slate-100 bg-white hover:border-emerald-300 hover:bg-emerald-50/30"
                                )}
                            >
                                <div className={cn(
                                    "p-2 sm:p-3 rounded-full shrink-0",
                                    mode === 'utmb' ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-600"
                                )}>
                                    <Download size={20} className="sm:hidden" />
                                    <Download size={24} className="hidden sm:block" />
                                </div>
                                <div className="text-left sm:text-center">
                                    <span className="font-bold text-slate-900 text-sm sm:text-base">{t('create.modeSelect.utmb')}</span>
                                    <span className="block text-xs text-slate-500">{t('create.modeSelect.utmbDesc')}</span>
                                </div>
                            </button>
                        </div>

                        {mode === 'utmb' && (
                            <UtmbRaceSearch onSelect={handleSelectUtmbRace} />
                        )}
                    </div>
                )}

                {step === 'form' && (
                    <RaceForm
                        initialValues={mode === 'utmb' ? utmbInitialValues : undefined}
                        onSubmit={mode === 'utmb' ? handleUtmbCreate : handleManualCreate}
                        isLoading={isLoading}
                        submitLabel={t('create.submit')}
                        lockedFields={mode === 'utmb' ? ['raceFormat', 'distance', 'elevationGain'] : []}
                        draftKey={mode === 'manual' ? 'create-race' : undefined}
                    />
                )}
            </PageContent>
        </Page>
    );
}
